import { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { formatBannerMessage } from "../utils/formatBannerMessage.js"

const CACHE_KEY = "kopy_banner_cache"
const DISMISSED_KEY = "kopy_dismissed_banners"
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

function getCachedBanner() {
	try {
		const cached = localStorage.getItem(CACHE_KEY)
		if (!cached) return null

		const { banner, timestamp } = JSON.parse(cached)
		const now = Date.now()

		if (now - timestamp > CACHE_TTL_MS) {
			localStorage.removeItem(CACHE_KEY)
			return null
		}

		return banner
	} catch {
		return null
	}
}

function setCachedBanner(banner) {
	try {
		localStorage.setItem(
			CACHE_KEY,
			JSON.stringify({
				banner,
				timestamp: Date.now(),
			}),
		)
	} catch {}
}

function getDismissedBanners() {
	try {
		const dismissed = localStorage.getItem(DISMISSED_KEY)
		return dismissed ? JSON.parse(dismissed) : []
	} catch {
		return []
	}
}

function addDismissedBanner(bannerId) {
	try {
		const dismissed = getDismissedBanners()
		if (!dismissed.includes(bannerId)) {
			dismissed.push(bannerId)
			localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed))
		}
	} catch {}
}

export function useGlobalBanner() {
	const { i18n } = useTranslation()
	const [banner, setBanner] = useState(null)
	const [isLoading, setIsLoading] = useState(true)

	const fetchBanner = useCallback(async () => {
		const cached = getCachedBanner()
		if (cached !== null) {
			const dismissed = getDismissedBanners()
			if (cached && dismissed.includes(cached.id)) {
				setBanner(null)
			} else {
				setBanner(cached)
			}
			setIsLoading(false)
			return
		}

		try {
			const response = await fetch("/api/banner/active")
			const data = await response.json()
			setCachedBanner(data.banner)

			if (data.banner) {
				const dismissed = getDismissedBanners()
				if (dismissed.includes(data.banner.id)) {
					setBanner(null)
				} else {
					setBanner(data.banner)
				}
			} else {
				setBanner(null)
			}
		} catch (error) {
			console.error("[useGlobalBanner] Failed to fetch banner:", error)
			setBanner(null)
		} finally {
			setIsLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchBanner()
	}, [fetchBanner])

	const dismiss = useCallback(() => {
		if (banner?.id) {
			addDismissedBanner(banner.id)
			setBanner(null)
		}
	}, [banner])

	const currentLanguage = i18n.language?.split("-")[0] || "en"
	const message = formatBannerMessage(
		banner?.translations?.[currentLanguage] || banner?.translations?.en,
		i18n.language,
	)
	const linkText = banner?.linkText?.[currentLanguage] || banner?.linkText?.en

	// Backwards compat: null linkAction + existing linkUrl = treat as 'url'
	const linkAction = banner?.linkAction || (banner?.linkUrl ? "url" : null)

	return {
		banner: banner
			? {
					id: banner.id,
					type: banner.type,
					message,
					dismissible: banner.dismissible,
					linkAction,
					linkUrl: banner.linkUrl,
					linkText,
				}
			: null,
		isLoading,
		dismiss,
	}
}
